import type { EdgeSecretEdit } from "@sitescene/shared";

import {
	cloneElement,
	type ComponentProps,
	type InputHTMLAttributes,
	isValidElement,
	type ReactNode,
	useContext,
	useState,
} from "react";

import { Input } from "@/components/ui/input/input";
import { NativeSelect } from "@/components/ui/select/select-native";
import { TextArea } from "@/components/ui/textarea/textarea";

import {
	type Integration,
	newIntegration,
	type Source,
	sourceLabels,
	sourceTopics,
} from "./editor";
import { FormValidation } from "./form-validation";
export function IntegrationEditor({
	item,
	update,
}: {
	item: Integration;
	update: (item: Integration) => void;
}) {
	const f = item.fields;

	const patch = (fields: Partial<Integration["fields"]>) =>
		update({ ...item, fields: { ...f, ...fields } });
	const text = (key: keyof Integration["fields"], label: string) => (
		<Label key={key} text={label}>
			<Input
				onChange={(value) =>
					patch({
						[key]:
							value ||
							(["host", "name", "sensor_id"].includes(key) ? "" : null),
					})
				}
				value={typeof f[key] === "string" ? f[key] : ""}
			/>
		</Label>
	);
	return (
		<section className="space-y-5">
			<h3 className="font-medium">
				{f.name || "New integration"} ·{" "}
				{f.integration_type === "milestone" ? "Milestone" : "Home Assistant"}
			</h3>
			<div className="grid gap-3 sm:grid-cols-2" key={f.integration_type}>
				<Label text="Integration type">
					<NativeSelect
						onChange={(event) => {
							const fresh = newIntegration(
								event.target.value === "milestone"
									? "milestone"
									: "homeassistant",
							);
							update({
								...fresh,
								fields: { ...fresh.fields, name: f.name },
								id: item.id,
								secrets: {
									auth_token: { operation: "remove" },
									onb_password: { operation: "remove" },
									password: { operation: "remove" },
								},
							});
						}}
						options={[
							{ label: "Home Assistant", value: "homeassistant" },
							{ label: "Milestone", value: "milestone" },
						]}
						value={f.integration_type}
					/>
				</Label>
				{text("name", "Integration name")}
				{text("host", "Host")}
				<Label text="Port">
					<NumericInput
						isReadOnly={f.integration_type === "milestone"}
						max={65535}
						min={1}
						onChange={(value) =>
							patch({
								port: value === "" ? Number.NaN : Number(value),
							})
						}
						type="number"
						value={f.port}
					/>
				</Label>
				{f.integration_type === "homeassistant" ? (
					<Secret
						edit={item.secrets.auth_token}
						name="Home Assistant access token"
						update={(value) =>
							update({
								...item,
								secrets: { ...item.secrets, auth_token: value },
							})
						}
					/>
				) : (
					<>
						{text("username", "Username")}
						<Secret
							edit={item.secrets.password}
							name="Milestone password"
							update={(value) =>
								update({
									...item,
									secrets: { ...item.secrets, password: value },
								})
							}
						/>
						{text("onb_host", "Video bridge host (optional)")}
						{text("onb_username", "Video bridge username")}
						<Label text="Video bridge port">
							<NumericInput
								onChange={(value) =>
									patch({
										onb_port: value === "" ? Number.NaN : Number(value),
									})
								}
								type="number"
								value={f.onb_port}
							/>
						</Label>
						<Secret
							edit={item.secrets.onb_password}
							name="Video bridge password"
							update={(value) =>
								update({
									...item,
									secrets: { ...item.secrets, onb_password: value },
								})
							}
						/>
						<Label text="CA certificates (PEM, optional)">
							<TextArea
								onChange={(value) => patch({ ca_bundle_pem: value || null })}
								rows={4}
								value={f.ca_bundle_pem ?? ""}
							/>
						</Label>
					</>
				)}
				<label className="flex gap-2 text-sm text-secondary">
					<input
						checked={f.tls_verify}
						onChange={(event) => patch({ tls_verify: event.target.checked })}
						type="checkbox"
					/>
					Verify TLS certificates
				</label>
			</div>
			<p className="text-xs text-secondary">
				Changing a host, username, or authentication type requires new
				credentials.
			</p>
		</section>
	);
}
function FormSection({
	children,
	columns = 2,
	description,
	title,
}: {
	children: ReactNode;
	columns?: 2 | 3;
	description?: string;
	title: string;
}) {
	return (
		<fieldset className="min-w-0 space-y-3 border-t border-secondary pt-4">
			<legend className="px-1 font-semibold text-primary">{title}</legend>
			{description && <p className="text-sm text-secondary">{description}</p>}
			<div
				className={`grid gap-4 ${columns === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}
			>
				{children}
			</div>
		</fieldset>
	);
}
const fieldNames: Record<string, string> = {
	"API Gateway host": "sensor_host",
	"Batch window (seconds)": "segment_duration",
	"CA certificates (PEM, optional)": "ca_bundle_pem",
	"Connection method": "integration_id",
	"Event topic (optional)": "sensor_event_topic",
	"Home Assistant access token": "auth_token",
	"Home Assistant entity ID": "ha_entity_id",
	"Home Assistant token": "sensor_password",
	Host: "sensor_host",
	"Integration name": "name",
	"Milestone password": "password",
	Port: "sensor_port",
	"RTSP stream URL": "rtsp_url",
	"Segment duration (seconds)": "segment_duration",
	"Segment format": "segment_format",
	"Sensor ID": "sensor_id",
	"Source name": "name",
	"Source password": "sensor_password",
	"Target bitrate (kbps)": "video_bitrate",
	"Target FPS": "video_fps",
	"Target resolution": "video_resolution",
	Username: "sensor_username",
	"Video bridge host (optional)": "onb_host",
	"Video bridge password": "onb_password",
	"Video bridge port": "onb_port",
	"Video bridge username": "onb_username",
	"Video Sensor ID": "video_sensor_id",
	"Zone ID": "zone_id",
};
export function SourceEditor({
	installation,
	integrations,
	item,
	sourceUrl,
	sourceUrlUnreadable = false,
	update,
}: {
	installation: string;
	integrations: Integration[];
	item: Source;
	sourceUrl?: string;
	sourceUrlUnreadable?: boolean;
	update: (item: Source) => void;
}) {
	const f = item.fields;

	const patch = (fields: Partial<Source["fields"]>) =>
		update({ ...item, fields: { ...f, ...fields } });
	const text = (
		key: keyof Source["fields"],
		label: string,
		placeholder?: string,
	) => (
		<Label key={key} text={label}>
			<Input
				onChange={(value) =>
					patch({
						[key]:
							value ||
							(["host", "name", "sensor_id"].includes(key) ? "" : null),
					})
				}
				placeholder={placeholder}
				value={typeof f[key] === "string" ? f[key] : ""}
			/>
		</Label>
	);
	const number = (
		key: keyof Source["fields"],
		label: string,
		min: number,
		max: number,
	) => (
		<Label key={key} text={label}>
			<NumericInput
				max={max}
				min={min}
				onChange={(value) =>
					patch({
						[key]: value === "" ? null : Number(value),
					})
				}
				type="number"
				value={typeof f[key] === "number" ? f[key] : ""}
			/>
		</Label>
	);
	const boolean = (key: keyof Source["fields"], label: string) => (
		<label className="flex items-center gap-2 text-sm text-secondary" key={key}>
			<input
				checked={f[key] === true}
				onChange={(event) => patch({ [key]: event.target.checked })}
				type="checkbox"
			/>
			{label}
		</label>
	);
	const linked = !!f.integration_id;
	const video = f.source_type === "rtsp_camera";
	const homeAssistant = f.source_type.startsWith("homeassistant");
	const milestone = f.source_type === "milestone_event";
	const sceneEvents = f.source_type === "axis_scene_events";
	const eventDriven = homeAssistant || milestone || sceneEvents;
	const matchingIntegrations = integrations.filter(
		(integration) =>
			integration.fields.integration_type ===
			(milestone ? "milestone" : "homeassistant"),
	);
	return (
		<section className="space-y-5">
			<h3 className="font-medium">
				{f.name || "New source"} · {sourceLabels[f.source_type]}
			</h3>
			<Label text="Source type">
				<Input isReadOnly value={sourceLabels[f.source_type]} />
			</Label>
			<div className="grid gap-4 sm:grid-cols-2">
				{text("name", "Source name", "e.g. Loading dock camera")}
				{item.desiredState === "stopped" && (
					<p>This source is stopped. Saving preserves its stopped state.</p>
				)}
			</div>
			<FormSection
				columns={3}
				description="Installation is set by your selection. Use the zone and sensor IDs assigned in SiteScene."
				title="SiteScene S3 Flow Configuration"
			>
				<Label text="Installation ID">
					<Input inputClassName="font-mono" isReadOnly value={installation} />
				</Label>
				{text("zone_id", "Zone ID", "zone-001")}
				{text("sensor_id", "Sensor ID", "sensor-001")}
				{sceneEvents &&
					text("video_sensor_id", "Video Sensor ID", "camera-001")}
				<p className="text-xs text-secondary sm:col-span-3">
					{sceneEvents
						? "Zone ID and Video Sensor ID are required for scene events. Video Sensor ID identifies the camera producing these events."
						: video
							? "Zone ID and MP4 format are required for video delivery."
							: "Zone ID is optional for this source."}
				</p>
				<div className="sm:col-span-3">
					{boolean("skip_upload", "Keep this source local; skip delivery")}
				</div>
			</FormSection>
			<FormSection
				description="Lookout connects from the installation's network after publication."
				key={`${f.source_type}:${f.integration_id ?? "direct"}`}
				title="Connection"
			>
				{video &&
					sourceUrlUnreadable &&
					item.secrets.rtsp_url?.operation === "keep" && (
						<p className="text-sm text-secondary" role="status">
							The saved stream URL can't be read with the current encryption
							key. Enter it again to replace it, or remove this source.
						</p>
					)}
				{video ? (
					<Label text="RTSP stream URL">
						<Input
							autoComplete="off"
							className="w-full min-w-0"
							onChange={(value) =>
								update({
									...item,
									secrets: {
										...item.secrets,
										rtsp_url:
											value === sourceUrl &&
											item.secrets.rtsp_url?.operation === "keep"
												? item.secrets.rtsp_url
												: { operation: "replace", value },
									},
								})
							}
							ref={(input) => {
								input?.setAttribute("autocapitalize", "none");
							}}
							spellCheck="false"
							type="text"
							value={
								item.secrets.rtsp_url?.operation === "replace"
									? item.secrets.rtsp_url.value
									: item.secrets.rtsp_url?.operation === "keep"
										? (sourceUrl ?? "")
										: ""
							}
						/>
					</Label>
				) : (
					<>
						{!f.source_type.startsWith("axis_") &&
							(matchingIntegrations.length || linked ? (
								<Label text="Connection method">
									<NativeSelect
										onChange={(event) =>
											update({
												...item,
												fields: {
													...f,
													ca_bundle_pem: null,
													integration_id: event.target.value || null,
													sensor_host: null,
													sensor_username: null,
												},
												secrets: {
													...item.secrets,
													sensor_password: { operation: "remove" },
												},
											})
										}
										options={[
											{ label: "Direct credentials", value: "" },
											...(linked &&
											!matchingIntegrations.some(
												(integration) => integration.id === f.integration_id,
											)
												? [
														{
															label:
																"Integration unavailable. Choose another connection.",
															value: f.integration_id ?? "",
														},
													]
												: []),
											...matchingIntegrations.map((integration) => ({
												label: integration.fields.name || "Unnamed integration",
												value: integration.id,
											})),
										]}
										value={f.integration_id ?? ""}
									/>
								</Label>
							) : (
								<p className="text-sm text-secondary sm:col-span-2">
									Using direct credentials. Add a shared integration below to
									reuse a connection across sources.
								</p>
							))}
						{!linked && (
							<>
								{text(
									"sensor_host",
									milestone ? "API Gateway host" : "Host",
									milestone ? "xprotect.customer.local" : "192.168.1.100",
								)}
								{milestone ? (
									<Label text="Port">
										<Input isReadOnly value="443" />
									</Label>
								) : (
									number("sensor_port", "Port", 1, 65535)
								)}
								{!homeAssistant &&
									text("sensor_username", "Username", "lookout-svc")}
								<Secret
									edit={item.secrets.sensor_password}
									name={
										homeAssistant ? "Home Assistant token" : "Source password"
									}
									update={(value) =>
										update({
											...item,
											secrets: { ...item.secrets, sensor_password: value },
										})
									}
								/>
								{milestone && (
									<p className="text-xs text-secondary sm:col-span-2">
										Use a dedicated XProtect Basic user. Windows/AD
										authentication is not supported.
									</p>
								)}
							</>
						)}
						{homeAssistant &&
							text(
								"ha_entity_id",
								"Home Assistant entity ID",
								f.source_type === "homeassistant_lock"
									? "lock.front_door"
									: f.source_type === "homeassistant_contact"
										? "binary_sensor.front_door"
										: "event.doorbell",
							)}
						{f.source_type.startsWith("axis_") && (
							<>
								{text(
									"sensor_event_topic",
									"Event topic (optional)",
									sourceTopics[f.source_type],
								)}
								<p className="text-xs text-secondary sm:col-span-2">
									Leave the topic empty to use the source default. A custom
									topic replaces that filter.
								</p>
							</>
						)}
						{!linked && boolean("tls_verify", "Verify TLS certificates")}
						{milestone && !linked && (
							<Label text="CA certificates (PEM, optional)">
								<TextArea
									onChange={(value) => patch({ ca_bundle_pem: value || null })}
									rows={4}
									value={f.ca_bundle_pem ?? ""}
								/>
							</Label>
						)}
					</>
				)}
			</FormSection>

			<FormSection title={video ? "Recording" : "Event delivery"}>
				{eventDriven ? (
					<p className="text-sm text-secondary sm:col-span-2">
						JSON format · one file per event or state change. Lookout ignores
						the batch window for these events.
					</p>
				) : (
					number(
						"segment_duration",
						video ? "Segment duration (seconds)" : "Batch window (seconds)",
						1,
						300,
					)
				)}
				{video ? (
					<Label text="Segment format">
						<NativeSelect
							onChange={(event) =>
								patch({
									segment_format: event.target.value === "ts" ? "ts" : "mp4",
								})
							}
							options={[
								{ label: "MP4", value: "mp4" },
								{ label: "TS (local recording only)", value: "ts" },
							]}
							value={f.segment_format}
						/>
					</Label>
				) : (
					!eventDriven && (
						<p className="self-center text-sm text-secondary">
							JSON format is selected automatically.
						</p>
					)
				)}
			</FormSection>
			{video && (
				<details className="rounded-lg border border-secondary p-4">
					<summary className="cursor-pointer font-medium">
						Bandwidth and audio (optional)
					</summary>
					<p className="my-3 text-sm text-secondary">
						Leave these settings empty to use Lookout's recording defaults. Set
						overrides only when the source needs them.
					</p>
					<div className="grid gap-4 sm:grid-cols-3">
						{text("video_resolution", "Target resolution", "1920x1080")}
						{number("video_fps", "Target FPS", 1, 60)}
						{number("video_bitrate", "Target bitrate (kbps)", 100, 50000)}
					</div>
					<div className="mt-4 space-y-3">
						{boolean("video_passthrough", "Copy video without transcoding")}
						<p className="text-xs text-secondary">
							Passthrough requires a browser-compatible camera encoding (H.264).
							Control bitrate on the camera; Lookout's transcoding size budget
							does not apply.
						</p>
						{boolean("audio_enabled", "Include audio")}
						{boolean("tls_verify", "Verify TLS certificates for secure RTSP")}
					</div>
				</details>
			)}
		</section>
	);
}
const fieldHints: Record<string, string> = {
	segment_duration: "Whole seconds, 1–300. Default: 60.",
	video_bitrate: "Whole kbps, 100–50000. Leave empty for the default.",
	video_fps: "Whole frames per second, 1–60. Leave empty for the default.",
	video_resolution:
		"Width x height without spaces, for example 1920x1080. Leave empty for the default.",
};
function Label({
	children,
	suffix = "",
	text,
}: {
	children: ReactNode;
	suffix?: string;
	text: string;
}) {
	const context = useContext(FormValidation);
	let field = fieldNames[text] ?? text;
	if (context.integration && ["Host", "Port", "Username"].includes(text))
		field = text.toLowerCase();
	const id = `${context.id}-${field.replace(/ /g, "-")}${suffix}`;
	const error = context.errors[field];
	const hint = fieldHints[field];
	return (
		<div
			className="flex min-w-0 flex-col gap-1 text-sm text-secondary"
			data-field={field}
		>
			<label htmlFor={id}>{text}</label>
			{isValidElement<
				ComponentProps<typeof Input> &
					Pick<InputHTMLAttributes<HTMLInputElement>, "aria-invalid">
			>(children)
				? cloneElement(children, {
						"aria-describedby":
							[hint ? `${id}-hint` : "", error ? `${id}-error` : ""]
								.filter(Boolean)
								.join(" ") || undefined,
						"aria-errormessage": error ? `${id}-error` : undefined,
						"aria-invalid": !!error,
						"aria-label": children.props["aria-label"] ?? text,
						...(children.type !== NativeSelect ? { isInvalid: !!error } : {}),
						id,
						...(children.type === Input || children.type === NumericInput
							? {
									inputClassName: `${children.props.inputClassName ?? ""} ${children.props.isReadOnly ? "font-mono" : ""}`,
									wrapperClassName: children.props.isReadOnly
										? "border-dashed bg-secondary"
										: undefined,
								}
							: {}),
					})
				: children}
			{hint && (
				<span className="text-xs text-secondary" id={`${id}-hint`}>
					{hint}
				</span>
			)}
			{error && (
				<span className="text-sm text-error-primary" id={`${id}-error`}>
					{error}
				</span>
			)}
		</div>
	);
}

function NumericInput(
	props: Omit<ComponentProps<typeof Input>, "value"> & {
		max?: number;
		min?: number;
		value?: number | string;
	},
) {
	const [raw, setRaw] = useState(() =>
		typeof props.value === "number" && Number.isNaN(props.value)
			? ""
			: String(props.value ?? ""),
	);
	return (
		<Input
			{...props}
			inputMode="numeric"
			onChange={(value) => {
				setRaw(value);
				props.onChange?.(value);
			}}
			type="text"
			value={raw}
		/>
	);
}

function Secret({
	edit,
	kind = "credential",
	name,
	update,
}: {
	edit: EdgeSecretEdit | undefined;
	kind?: "credential" | "URL";
	name: string;
	update: (value: EdgeSecretEdit) => void;
}) {
	const [retained, setRetained] = useState(
		edit?.operation === "keep" ? edit.version : undefined,
	);
	if (edit?.operation === "keep" && edit.version !== retained)
		setRetained(edit.version);
	return (
		<div className="space-y-1">
			{retained ? (
				<Label suffix="-operation" text={name}>
					<NativeSelect
						onChange={(event) => {
							if (event.target.value === "keep" && retained)
								update({ operation: "keep", version: retained });
							else if (event.target.value === "replace")
								update({ operation: "replace", value: "" });
							else update({ operation: "remove" });
						}}
						options={[
							...(retained
								? [{ label: `Keep stored ${kind}`, value: "keep" }]
								: []),
							{ label: `Set new ${kind}`, value: "replace" },
							{ label: `No ${kind} / remove`, value: "remove" },
						]}
						value={edit?.operation ?? "remove"}
					/>
				</Label>
			) : (
				<span className="text-sm text-secondary">{name}</span>
			)}
			{(!retained || edit?.operation === "replace") && (
				<Label text={name}>
					<Input
						aria-label={`${name} value`}
						autoComplete="off"
						className="w-full"
						onChange={(value) =>
							update(
								value
									? { operation: "replace", value }
									: { operation: "remove" },
							)
						}
						type="password"
						value={edit?.operation === "replace" ? edit.value : ""}
					/>
				</Label>
			)}
		</div>
	);
}
